import { CloudUploadIcon, FileNativeInput, SparksAIIcon, Typography } from "@cpj-ai/panel-npm-package";
import { Button } from "@heroui/react";
import React, { useCallback, useRef } from "react";
import toast from "react-hot-toast";

interface UploadFileProps {
  resumeFile: File | null;
  setResumeFile: (file: File | null) => void;
}

export const UploadFile: React.FC<UploadFileProps> = ({ resumeFile, setResumeFile }) => {
  const inputElementRef = useRef<HTMLInputElement>(null);

  const openImagePicker = useCallback(() => {
    inputElementRef.current?.click();
  }, []);

  const checkValidation = (file?: File) => {
    if (file?.type !== "application/pdf") {
      toast.error("Please select a PDF file");
      return false;
    }
    if (file?.size > 1024 * 1024 * 5) {
      toast.error("File size must be less than 5MB");
      return false;
    }
    return true;
  };

  const handleSelectImage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (!file) return;

      const isFileValid = checkValidation(file);
      if (isFileValid) setResumeFile(file);
    },
    [setResumeFile],
  );

  const handleDelete = useCallback(() => {
    setResumeFile(null);
  }, [setResumeFile]);

  return (
    <div className="w-full">
      <FileNativeInput ref={inputElementRef} onChange={handleSelectImage} accept="application/pdf" />

      {resumeFile ? (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-3 md:p-6">
          <div className="flex flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <Typography className="font-semibold text-gray-900 text-sm md:text-base truncate">
                  {resumeFile.name}
                </Typography>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-1">
                  <Typography className="text-xs text-gray-500">
                    {(resumeFile.size / (1024 * 1024)).toFixed(1)} MB • PDF
                  </Typography>
                  <div className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full inline-block w-fit">
                    Ready for AI Analysis
                  </div>
                </div>
              </div>
            </div>
            <Button
              onPress={handleDelete}
              isIconOnly
              variant="light"
              size="sm"
              className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl self-end sm:self-center min-h-10 min-w-10"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </Button>
          </div>
        </div>
      ) : (
        <div
          className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-4 md:p-8 text-center"
          onClick={openImagePicker}
        >
          <div className="w-10 h-10 md:w-16 md:h-16 bg-primary-500 rounded-xl flex items-center justify-center mx-auto mb-4 md:mb-6">
            <CloudUploadIcon className="fill-white w-7 h-7 md:w-8 md:h-8" viewBox="0 0 50 50" />
          </div>

          <Typography className="text-md md:text-xl font-semibold text-gray-900 mb-2">Upload Resume</Typography>
          <Typography className="text-xs md:text-base text-gray-600 mb-4 md:mb-6">
            <span className="md:hidden">Tap to select your PDF</span>
            <span className="hidden md:inline">Click to select your PDF</span>
          </Typography>

          <div className="flex items-center justify-center gap-3 mb-4 md:mb-8 text-xs md:text-sm text-gray-500">
            <span>PDF only</span>
            <span>•</span>
            <span>Max 5MB</span>
          </div>

          <Button
            onPress={openImagePicker}
            className="bg-primary-500 text-white rounded-lg px-8 py-3 md:px-10 md:py-4 font-medium w-full md:w-auto"
            startContent={<CloudUploadIcon width={20} className="fill-white" viewBox="0 0 45 45" />}
          >
            Choose File
          </Button>

          <div className="flex items-center justify-center gap-1 mt-4 md:mt-6 text-xs md:text-sm text-gray-500">
            <SparksAIIcon viewBox="0 0 22 22" className="w-3 h-3 md:w-4 md:h-4 stroke-blue-500" />

            <span>AI analysis & secure</span>
          </div>
        </div>
      )}
    </div>
  );
};
